import type { ComponentType } from 'react'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'

import LoginView from '@/auth/LoginView'
import { RequireAuth } from '@/auth/RequireAuth'
import { ModuleGuard } from '@/modules/ModuleGuard'
import type { ModuleKey } from '@/modules/module-keys'
import { getModule } from '@/modules/registry'
import { RequireAdmin } from '@/modules/RequireAdmin'

import { AppLayout } from './AppLayout'
import { NotFound, RouteError } from './RouteError'

/**
 * Una rotta per modulo, generata dal registro.
 *
 * Il percorso non viene riscritto qui: è quello dichiarato nel registro, così
 * navigazione e routing non possono divergere. La vista arriva in lazy — il
 * codice di Amministratore non pesa su chi apre Ricerca — ed è sempre avvolta
 * dal guardiano del modulo.
 */
function moduleRoute(
  key: ModuleKey,
  load: () => Promise<{ default: ComponentType }>,
  options?: { adminOnly?: boolean },
): RouteObject {
  const definition = getModule(key)

  return {
    path: definition.path.replace(/^\//, ''),
    lazy: async () => {
      const { default: View } = await load()

      function GuardedView() {
        const view = (
          <ModuleGuard module={key}>
            <View />
          </ModuleGuard>
        )
        return options?.adminOnly ? <RequireAdmin>{view}</RequireAdmin> : view
      }
      GuardedView.displayName = `Guarded(${definition.name})`

      return { Component: GuardedView }
    },
  }
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginView /> },

  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/ricerca" replace /> },

      moduleRoute('ricerca', () => import('@/modules/ricerca/RicercaView')),
      moduleRoute('collection', () => import('@/modules/collection/CollectionView')),
      moduleRoute('presentazione', () => import('@/modules/presentazione/PresentazioneView')),

      moduleRoute('analitica', () => import('@/modules/analitica/AnaliticaView')),
      moduleRoute('trends', () => import('@/modules/trends/TrendsView')),
      moduleRoute('recommender', () => import('@/modules/recommender/RecommenderView')),
      moduleRoute('onirica', () => import('@/modules/onirica/OniricaView')),
      moduleRoute('generativa', () => import('@/modules/generativa/GenerativaView')),

      moduleRoute('utilizzo', () => import('@/modules/utilizzo/UtilizzoView')),
      moduleRoute('impostazioni', () => import('@/modules/impostazioni/ImpostazioniView')),
      moduleRoute('amministratore', () => import('@/modules/amministratore/AmministratoreView')),
      moduleRoute('aziende', () => import('@/modules/aziende/AziendeView'), { adminOnly: true }),

      { path: '*', element: <NotFound /> },
    ],
  },
])
